import { PortfolioChart } from '@/components/portal-modules/PortalChart';
import { useAppSelector } from '@/state/hooks';
import { selectCurrencyConfigs } from '@/state/reducers/ohoSlice';
import { BalancesSummaryReturn } from '@/types/sso';
import useLanguage from '@/utils/hooks/useLanguage';
import { useMemo } from 'react';
// Professional financial palette
const COLORS = [
    '#0ea5e9',
    '#6366f1',
    '#10b981',
    '#f59e0b',
    '#ec4899',
    '#8b5cf6',
    '#14b8a6',
    '#f97316',
    '#64748b',
    '#d946ef'
];

export interface Position {
    id: string;
    instrument: string;
    assetClass: 'Equity' | 'FX' | 'Fixed Income' | 'Commodity' | 'Crypto';
    instrumentCcy: string;
    marketValue: number; // in base currency (USD)
}

interface PortalBalancesProps {
    balances?: BalancesSummaryReturn;
    netEquity?: {
        netEquity: number;
    };
}

function PortalBalances({ balances, netEquity }: PortalBalancesProps) {
    const { balances: balancesData, baseCurrency } = balances || {};

    const currencyConfigs = useAppSelector(selectCurrencyConfigs);

    const lang = useLanguage();

    const totalBalancesValue = useMemo(() => {
        if (balancesData) {
            return balancesData.reduce((sum, balance) => {
                const itemValue =
                    balance.subRows?.reduce((subSum, sub) => subSum + Math.abs(sub.amountInBaseCurrency || 0), 0) ||
                    Math.abs(balance.amountInBaseCurrency || 0);
                return sum + itemValue;
            }, 0);
        }
        return 0;
    }, [balances]);

    const assetClassData = useMemo(() => {
        if (balancesData) {
            const map = new Map<string, number>();
            balancesData.forEach((balance) => {
                const config = currencyConfigs.find(
                    (cfg) => cfg.show === balance.currencyName || cfg.code === balance.currencyName
                );
                const keyLabel = config?.type || 'Others';

                const currentValue = map.get(keyLabel) || 0;
                const itemValue =
                    balance.subRows?.reduce((sum, sub) => sum + Math.abs(sub.amountInBaseCurrency || 0), 0) ||
                    Math.abs(balance.amountInBaseCurrency || 0);
                map.set(keyLabel, currentValue + itemValue);
            });

            const result = Array.from(map.entries()).map(([name, value], index) => ({
                name,
                value,
                percentage: (value / totalBalancesValue) * 100,
                color: COLORS[index % COLORS.length]
            }));
            // .filter((item) => item.value > 0);

            return result.sort((a, b) => b.value - a.value);
        }
    }, [currencyConfigs, totalBalancesValue, balancesData]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Asset Class Distribution */}
            {balances && assetClassData && (
                <div className="h-full">
                    <PortfolioChart
                        title={lang.portal.assetClassAllocation}
                        data={assetClassData}
                        totalValue={totalBalancesValue}
                        baseCurrency={baseCurrency || ''}
                    />
                </div>
            )}
        </div>
    );
}

export default PortalBalances;
